<script setup lang="ts">
  import { Icon } from '@iconify/vue'
  import { useI18n } from 'vue-i18n'

  import { CompaniesService, OpenAPI } from '@/client'
  import type { Option } from '@/components/sm/SmSelect.vue'
  import { useGlobalStore } from '@/stores/globalStore'
  import { currencyCodeToIconName } from '@/utils/currency/currencyUtil'

  import { sendToast } from '../sm/SmNotification'

  const i18n = useI18n()
  const globalStore = useGlobalStore()

  const visible = defineModel('visible', {
    default: false,
    type: Boolean,
  })
  const createDemoData = defineModel('createDemoData', {
    default: false,
    type: Boolean,
  })
  const companyName = defineModel('companyName', {
    default: '',
    type: String,
  })

  const loading = ref(false)
  type HostingRegion = 'EU' | 'US'
  const hostingOptions: Option<HostingRegion>[] = [
    { value: 'EU', label: i18n.t('hostingRegionEu') },
    { value: 'US', label: i18n.t('hostingRegionUs') },
  ]
  const hostingSelection = ref<Option<HostingRegion>[]>([hostingOptions[0]])

  const error = ref({
    message: '',
    visible: false,
  })

  const emit = defineEmits(['companyCreated'])

  async function createCompany() {
    if (loading.value) return
    if (!hostingSelection.value[0]) {
      error.value = {
        message: i18n.t('hostingRegionRequired'),
        visible: true,
      }
      return
    }
    loading.value = true
    const previousBase = OpenAPI.BASE
    const selectedRegion = hostingSelection.value[0].value
    const isProduction = import.meta.env.MODE === 'production'
    if (!isProduction) {
      OpenAPI.BASE = globalStore.getIp
    } else if (selectedRegion === 'US') {
      OpenAPI.BASE = globalStore.productionUsApi
    } else {
      OpenAPI.BASE = globalStore.productionApi
    }

    try {
      const response = await CompaniesService.createCompanyCompaniesPost({
        requestBody: { name: companyName.value, demo: createDemoData.value },
      })
      sendToast(i18n.t('companyCreated'), undefined, 'success')
      emit('companyCreated', response)
      visible.value = false
    } catch (_error) {
      error.value = {
        message: i18n.t('errorCreatingCompany'),
        visible: true,
      }
    } finally {
      OpenAPI.BASE = previousBase
      loading.value = false
    }
  }

  function getRegionIconName(region: HostingRegion) {
    const currencyCode = region === 'US' ? 'USD' : 'EUR'
    return currencyCodeToIconName(currencyCode)
  }

  watch(
    () => visible.value,
    (newValue) => {
      if (!newValue) {
        error.value = {
          message: '',
          visible: false,
        }
        companyName.value = ''
        createDemoData.value = false
        hostingSelection.value = [hostingOptions[0]]
      }
    }
  )
</script>

<template>
  <SmDialog
    v-model:visibility="visible"
    :title="i18n.t('createCompany')"
    size="small">
    <div class="flex flex-col items-start gap-4">
      <SmDialogMessage
        :message="error.message"
        :visible="error.visible"
        type="error" />

      <div style="width: 99%">
        <SmInput
          v-model="companyName"
          size="medium"
          :label="i18n.t('companyName')"
          :design="'form'"
          outline />
      </div>

      <div style="width: 99%">
        <SmSelect
          v-model:selection="hostingSelection"
          class="w-full"
          :options="hostingOptions"
          :label="i18n.t('hostingRegion')">
          <template #trigger="{ items }">
            <div class="flex items-center gap-2">
              <Icon
                v-if="items?.[0]"
                :icon="getRegionIconName(items[0].value)"
                scale="0.8" />
              <span>
                {{ items?.[0]?.label || i18n.t('hostingRegion') }}
              </span>
            </div>
          </template>

          <template #item="{ item }">
            <div class="flex items-center gap-2">
              <Icon :icon="getRegionIconName(item.value)" scale="0.8" />
              <span>{{ item.label }}</span>
            </div>
          </template>
        </SmSelect>
      </div>

      <div class="flex items-center gap-3">
        <SmSwitch v-model="createDemoData" />
        <p class="pt-1">{{ i18n.t('demoData') }}</p>
      </div>
    </div>

    <template #footer>
      <div style="margin-top: 1rem; display: flex; gap: 1rem">
        <SmButton outline @click="visible = false">
          {{ i18n.t('cancel') }}
        </SmButton>
        <SmButton type="primary" :loading="loading" @click="createCompany">
          {{ i18n.t('create') }}
        </SmButton>
      </div>
    </template>
  </SmDialog>
</template>
